/**
 * 试卷「给分点(scorePointList)」内容的按小题注入。
 *
 * 组卷时，写作类小题的「作文要求 / 要求附件」并不写在正文 HTML 里，
 * 而是挂在 flows[].scorePointList 上：
 *   - scorePointList[].simpleTaskSaveDto.content / .contentUrl      → 作文要求 / 要求附件
 * 预览/答题时需要把这些内容插到该小题的「空位」之前（空位即 [name=tagId] 的 input/textarea），
 * 否则学生只看到一条空线，不知道要做什么。
 *
 * 注：原本还会注入「题目」(scorePointList[].sentenceSaveDto.question)，
 * 现已按要求去掉——只保留作文要求 / 要求附件。
 *
 * 使用方：
 *   - 教师端：preTask.vue（试卷预览页，最外层字段 exam.type）、
 *     PenAndComp.vue 的试卷预览模式（paperDetail=1，最外层字段 data.type）
 *   - 学生端：PenAndComp.vue 学生做作业（getData，最外层字段 data.paperType）
 * 展示范围（两道门，层级不同）：
 *   1. 试卷级：type / paperType 为 4 或 5（见 isFlowExtraPaperType）；
 *   2. 小题级：该小题自身最外层字段 knowledgeType 为 12（写作类，见 isWritingKnowledgeType）。
 *      ⚠️ knowledgeType 挂在**小题(flow)**上，不是试卷上——翻译(11)等小题的给分点里也可能带
 *      simpleTaskSaveDto，只认试卷级会把它们误展示成「作文要求」，所以必须在逐小题筛选时挡掉。
 *      与后台 english-user-management 的 components/StudentTask/index.vue 口径一致
 *      （String(flow.knowledgeType) !== "12" 直接 return）。
 * 必须放在答案回填（util/preTask.js 的 getInputSortUtil）之前调用——回填会把空位 input
 * 原地换成带答案的 span，那时 [name=tagId] 已经不存在，插不进去了。
 *
 * 注入节点的类名（essay-requirement）与后台
 * english-user-management 的 Preview、StudentTask 保持一致，两端口径统一；
 * 样式由各页面自行声明（注入节点不带 scoped 的 data-v 属性，scoped 样式需 ::v-deep）。
 */

/**清理上一轮注入节点用的选择器（重复渲染时避免叠加）**/
const INLINE_SELECTOR = ".essay-requirement--inline";

/**把字段值归一化成去空白的字符串，兼容后端返回数字 4 或字符串 "4" 两种写法**/
function normalizeField(value) {
  if (value === null || value === undefined) return "";
  return String(value).trim();
}

/**
 * 当前试卷是否需要展示「作文要求 / 要求附件」——试卷级门。
 * 只认试卷最外层字段 type（教师端）或 paperType（学生端）为 4 或 5 的试卷。
 */
export function isFlowExtraPaperType(value) {
  const v = normalizeField(value);
  return v === "4" || v === "5";
}

/**
 * 当前小题是否需要展示「作文要求 / 要求附件」——小题级门。
 * knowledgeType 挂在**小题(flow)**自己身上：12 = 写作（要展示作文要求），
 * 11 = 翻译等其它类型（即使给分点里带了 simpleTaskSaveDto 也不能展示）。
 */
export function isWritingKnowledgeType(value) {
  return normalizeField(value) === "12";
}

/**是否为有内容的文本（作文要求/要求附件地址）**/
export function hasText(text) {
  return text != null && String(text).trim() !== "";
}

/**
 * 从 flows 中挑出需要额外展示内容的小题：
 * 只处理 knowledgeType 为 12（写作）的小题，取数位置为
 * scorePointList[].simpleTaskSaveDto 的 content（作文要求富文本）与
 * contentUrl（要求附件图片），同一小题多个给分点都带配置时取最后一个有内容的。
 * 返回 [{ tagId, essay: { content, contentUrl } }]
 */
export function getFlowExtraList(flows) {
  const list = [];
  if (!Array.isArray(flows) || flows.length === 0) return list;
  flows.forEach(flow => {
    if (!flow || !flow.tagId) return;
    // 小题级门：knowledgeType 是小题自己最外层的字段，非 12（如翻译 11）一律跳过
    if (!isWritingKnowledgeType(flow.knowledgeType)) return;
    const scorePointList = Array.isArray(flow.scorePointList)
      ? flow.scorePointList
      : [];
    let essay = null;
    scorePointList.forEach(sp => {
      if (!sp) return;
      // 写作类给分点：作文要求 + 要求附件
      const dto = sp.simpleTaskSaveDto;
      if (dto && (hasText(dto.content) || hasText(dto.contentUrl))) {
        essay = {
          content: dto.content || "",
          contentUrl: dto.contentUrl || "",
        };
      }
    });
    if (essay) list.push({ tagId: flow.tagId, essay });
  });
  return list;
}

/**生成「作文要求/要求附件」节点（content 为富文本，contentUrl 为附件图片）**/
function createEssayRequirementNode(dto) {
  const node = document.createElement("div");
  node.className = "essay-requirement essay-requirement--inline";
  let html = "";
  if (dto.content) {
    html += '<div class="essay-requirement__title">作文要求</div>';
    html += `<div class="essay-requirement__content">${dto.content}</div>`;
  }
  if (dto.contentUrl) {
    html += '<div class="essay-requirement__subtitle">要求附件</div>';
    html += `<img class="essay-requirement__img" src="${dto.contentUrl}" alt="" />`;
  }
  node.innerHTML = html;
  return node;
}

/**
 * 把某小题的「作文要求 / 要求附件」插到该小题空位之前（找不到空位返回 false）。
 * 顺序：作文要求 → 原空位（空位随后被 getInputSortUtil 换成带答案的线）。
 */
function insertFlowExtra(container, extra) {
  if (!container || !extra || !extra.tagId) return false;
  const tag = container.querySelector(`[name="${extra.tagId}"]`);
  if (!tag || !tag.parentNode) return false;
  if (extra.essay) {
    tag.parentNode.insertBefore(
      createEssayRequirementNode(extra.essay),
      tag,
    );
  }
  return true;
}

/**把容器入参（DOM 元素 id 或元素本身）统一解析成有效元素数组**/
function resolveContainers(containers) {
  const nodes = Array.isArray(containers) ? containers : [];
  return nodes
    .map(item =>
      typeof item === "string" ? document.getElementById(item) : item,
    )
    .filter(node => node && node.nodeType === 1);
}

/**
 * 按小题渲染「作文要求 / 要求附件」。
 * @param {Array} flows 试卷的 flows / taskFlowList
 * @param {Array} containers 容器（DOM 元素 id 或元素本身），按先后顺序尝试定位空位，
 *        同名空位只插第一处，避免正文与答题卡重复渲染
 * 都找不到空位时兜底挂到第一个容器末尾，保证内容可见；
 * 重复渲染（重新 init）时先清掉上一轮注入的节点。
 */
export function renderFlowExtras(flows, containers) {
  const list = resolveContainers(containers);
  if (!list.length) return;
  const extras = getFlowExtraList(flows);
  if (!extras.length) return;
  list.forEach(container => {
    container
      .querySelectorAll(INLINE_SELECTOR)
      .forEach(node => node.parentNode && node.parentNode.removeChild(node));
  });
  const fallback = list[0];
  extras.forEach(extra => {
    const placed = list.some(container => insertFlowExtra(container, extra));
    if (placed) return;
    if (extra.essay) {
      fallback.appendChild(createEssayRequirementNode(extra.essay));
    }
  });
}

export default {
  hasText,
  isFlowExtraPaperType,
  isWritingKnowledgeType,
  getFlowExtraList,
  renderFlowExtras,
};
